import firebase from "firebase/compat/app";
import "firebase/compat/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = firebase.initializeApp(firebaseConfig);
const messaging = firebase.messaging();

// Register Firebase messaging SW at a dedicated scope so it doesn't conflict
// with the Vite PWA service worker registered at the root scope ("/").
// Both can coexist — Vite PWA handles caching/chunk recovery, Firebase handles
// background push notifications.
//
// requestForToken() must use THIS registration specifically, not
// navigator.serviceWorker.ready — .ready resolves to whichever worker
// controls the current page, which for any route outside "/firebase-sw/"
// is the root-scoped Vite PWA worker, not this one.
const firebaseSwRegistrationPromise: Promise<ServiceWorkerRegistration | null> =
  "serviceWorker" in navigator
    ? navigator.serviceWorker
        .register("/firebase-messaging-sw.js", { scope: "/firebase-sw/" })
        .then((registration) => {
          console.log("Firebase SW registered with scope:", registration.scope);
          return registration;
        })
        .catch((error) => {
          console.error("Firebase SW registration failed:", error);
          return null;
        })
    : Promise.resolve(null);

export const requestForToken = async () => {
  try {
    const registration = await firebaseSwRegistrationPromise;
    if (!registration) {
      console.error("Firebase service worker is not registered.");
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const token = await messaging.getToken({
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      return token;
    } else {
      console.log("User denied notification permission.");
      return null;
    }
  } catch (error) {
    console.error("Error getting FCM token:", error);
    return null;
  }
};

export interface FcmMessagePayload {
  notification?: {
    title?: string;
    body?: string;
  };
}

export const onMessageListener = (
  callback: (payload: FcmMessagePayload) => void
) =>
  messaging.onMessage((payload) => {
    console.log("Message received:", payload);
    callback(payload as FcmMessagePayload);
  });

export { messaging };
